import { useEffect, useRef } from "react";
import "../style/Space.css";
import { SPACE_IMAGES } from "../utils/spaceUtils";
import { useDispatch, useSelector } from "react-redux";
import { hasData } from "../utils/utils";
import { useMediaQuery } from "@mui/material";
import { breakpoint } from "../utils/responsiveUtils";
import { motion, useScroll, useTransform } from "framer-motion";
import LoadingBackdrop from "../components/skeleton/LoadingBackdrop";
import AddressSession from "../session/AddressSession";
import {
  fetchSpaceImages,
  fetchSpaceText,
} from "../api/space/space.reduxThunk";
import SlideWrapper from "../components/generics/SlideWrapper";
import SwipeWrapper from "../components/generics/SwipeWrapper";
import { slideUp } from "../utils/animationUtils";
import XoiComHeading from "../components/generics/XoiComHeading";
import NepSongSession from "../components/space/NepSongSession";
import OutsideSession from "../components/space/OutsideSession";
import NatureSession from "../components/space/NatureSession";
import PieceSession from "../components/space/PieceSession";
import LazyImage from "../components/generics/LazyImage";
import { fetchSpaceBG } from "../api/reduxThunk";
import useImageSlide from "../hooks/useImageSlide";
import usePageSetup from "../hooks/usePageSetup";

const { TREO_QUAN_AO } = SPACE_IMAGES;

function Space() {
  const dispatch = useDispatch();

  usePageSetup("light");

  const spaceBackground = useSelector((state) => state.reducer.spaceBackground);
  const spaceText = useSelector((state) => state.reducer.spaceText);
  const spaceImages = useSelector((state) => state.reducer.spaceImages);
  const spaceContent = useSelector((state) => state.reducer.spaceContent);
  const language = useSelector((state) => state.reducer.language);
  const sliderImages = spaceImages.slider;
  const parallaxImages = spaceImages.parallax;
  const { community, outside, nepSong, nature, piece } = spaceText;

  const loading = useSelector((state) => state.reducer.loading);

  useEffect(() => {
    dispatch(fetchSpaceImages());
    dispatch(fetchSpaceBG());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(fetchSpaceText());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const mobile = useMediaQuery(breakpoint.mobile);
  const { openSlide, selectedImage, handleOpenSlide, handleCloseSlide } =
    useImageSlide();

  const targetRef = useRef();
  const darkTransitionRef = useRef();
  const lightTransitionRef = useRef();

  const { scrollYProgress: scrollYProgressBackground } = useScroll({
    target: darkTransitionRef,
    offset: ["center end", "start start"],
  });

  const { scrollYProgress: scrollYProgressBackground2 } = useScroll({
    target: lightTransitionRef,
    offset: ["start start", "0.2 start"],
  });

  const backgroundColor = useTransform(
    scrollYProgressBackground,
    [0, 1],
    ["#FFFFFF", "#001624"]
  );
  const backgroundColor2 = useTransform(
    scrollYProgressBackground2,
    [0, 1],
    ["#001624", "#fffaf3"]
  );

  const outsideProps = {
    images: parallaxImages,
    content: outside,
    openSlide,
    handleOpenSlide,
    handleCloseSlide,
    selectedImage,
  };

  return (
    <div style={{ overflowX: "hidden" }}>
      <LoadingBackdrop open={loading} />
      <LazyImage
        className="landing-session"
        alt="space-landing"
        imageSrc={spaceBackground}
      />
      <motion.div style={{ backgroundColor: backgroundColor }}>
        <div className="space-community-session">
          <div className="space-community-text-container">
            <div className="space-community-heading">
              <XoiComHeading heading={community.heading} />
            </div>
            <div className="body-b1">{community.bodyText}</div>
          </div>
          {mobile && (
            <div className="space-outside-mobile">
              <OutsideSession {...outsideProps} />
            </div>
          )}
          <div className="khu-tap-the-graphic">
            <div className="stair"></div>
            <img
              className="treo-quan-ao"
              src={TREO_QUAN_AO}
              alt="treo-quan-ao"
            />
            {!mobile && (
              <motion.div
                className="space-outside"
                variants={slideUp}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
              >
                <OutsideSession {...outsideProps} />
              </motion.div>
            )}
          </div>
          <NepSongSession
            content={nepSong}
            target={targetRef}
            images={parallaxImages}
            handleOpenSlide={handleOpenSlide}
          />
        </div>
        <NatureSession
          backgroundRef={darkTransitionRef}
          content={nature}
          images={parallaxImages}
          handleOpenSlide={handleOpenSlide}
        />
      </motion.div>
      <motion.div
        ref={lightTransitionRef}
        className="space-nature-piece-transition"
        style={{ backgroundColor: backgroundColor2 }}
      ></motion.div>
      <PieceSession backgroundColor={backgroundColor2} content={piece} spaceContent={spaceContent} />
      {hasData(sliderImages) && (
        <div>
          {mobile ? (
            <div className="image-slide-session-mobile">
              <SwipeWrapper steps={sliderImages} mode="image-slide" />
            </div>
          ) : (
            <div className="image-slide-session">
              <SlideWrapper steps={sliderImages} mode="image-slide" />
            </div>
          )}
        </div>
      )}
      <AddressSession mode="cta" />
    </div>
  );
}

export default Space;
